import Vue from "vue";
import Vuex from "vuex";
import FormService from "../services/FormService";

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    locales: [
      { id: "en", name: "English" }
    ],
    form: null,
    token: null,
    activeLocale: "en",
    rtl: false,
    logoUrl: "",
    formModel: {},
    previousModel: {},
    files: [],
    errorMessage: null,
    formDisabled: false,
    hasSubmitted: false,
    response: null,
    uploadPercentage: 0,
    uploadFileIndex: 0,
  },
  getters: {
    allFilesDone: state => state.files.filter(f => !f.isDone).length === 0,
  },
  mutations: {
    setForm(state, form) {
      state.form = form;
    },
    setUploadToken(state, token) {
      state.token = token;
    },
    setResponse(state, response) {
      state.response = response;
    },
    setUploading(state, { fileIndex, status }) {
      const file = state.files[fileIndex];
      file.isUploading = status;
      Vue.set(state.files, fileIndex, file);
    },
    setFormDisabled(state, status) {
      state.formDisabled = status;
    },
    setHasSubmitted(state, status) {
      state.hasSubmitted = status;
    },
    setFileDone(state, fileIndex) {
      const file = state.files[fileIndex];
      file.isDone = true;
      Vue.set(state.files, fileIndex, file);
    },
    addFileError(state, { fileIndex, error }) {
      state.errorMessage = error.message;
      const file = state.files[fileIndex];
      file.hasError = true;
      Vue.set(state.files, fileIndex, file);
    },
    dismissErrorMessage(state) {
      state.errorMessage = "";
    },
    clearFileError(state, fileIndex) {
      state.errorMessage = null;
      const file = state.files[fileIndex];
      file.hasError = false;
      Vue.set(state.files, fileIndex, file);
    },
    setUploadPercentage(state, { fileIndex, value }) {
      const file = state.files[fileIndex];
      file.progress = value;
      Vue.set(state.files, fileIndex, file);
    },
    setUploadFileIndex(state, index) {
      state.uploadFileIndex = index;
    },
    setLocale(state, { activeLocale, rtl }) {
      state.activeLocale = activeLocale;
      state.rtl = rtl;
    },
    setLogo(state, logoUrl) {
      state.logoUrl = logoUrl;
    },
    resetLogo(state) {
      state.logoUrl = "";
    },
    setFormModel(state, model) {
      state.formModel = model;
    },
    saveModel(state) {
      state.previousModel = state.formModel;
    },
    clearPrevious(state) {
      state.previousModel = {};
    },
    clearCurrent(state) {
      state.formModel = {};
      state.errorMessage = null;
      state.formDisabled = false;
      state.hasSubmitted = false;
    },
    clearFiles(state) {
      state.files = [];
      state.formDisabled = false;
      state.hasSubmitted = false;
    },
    addFile(state, file) {
      state.files.push({
        ...file,
        isDone: false,
        isUploading: false,
        progress: 0,
        hasError: false,
        // TODO: track cancellation token of upload
      });
    },
    removeFile(state, i) {
      state.files.splice(i, 1);
      // state.fileErrors = state.fileErrors.filter(e => e.fileIndex !== i);
    }
  },
  actions: {
    async submitWebform({ commit, state, dispatch }, { webformId, ...model }) {
      // Disable the form while submitting
      commit("setFormDisabled", true);
      let token = "";
      let message = false;
      try {
        const result = await FormService.submitWebform({ webformId, ...model });
        token = result.token;
        message = result.message;
      } catch (error) {
        commit("setFormDisabled", false);
        return false;
      }
      // Submit of main form data now complete
      // This flag is used to check if should redirect to confirm when removing files
      commit("setHasSubmitted", true);
      commit("setResponse", message);

      // Conditionally upload attachments to the submitted form
      // (using token from previous response)
      const formHasFiles = state.form.fields
        .filter(({ type }) => type === "attachments")
        .length > 0;
      const attachments = state.files;
      const hasAttachments = attachments && attachments.length && attachments.length > 0;
      if (formHasFiles && hasAttachments && token) {
        commit("setUploadToken", token);

        await dispatch("sendAllAttachments", attachments);

        commit("setUploadFileIndex", attachments.length);
        if (state.files.filter(f => f.hasError).length === 0) {
          // if NO files have errors reenable the form because everything is fine
          commit("setFormDisabled", false);
        }
      } else {
        // nothing to upload so safe to reenable form
        commit("setFormDisabled", false);
      }

      if (!state.formDisabled) {
        // All submitted OK
        return message;
      }
      // Form is still disabled implies errors with attachments
      return false;
    },

    async sendAllAttachments({ dispatch }, attachments) {
      for (let fileIndex = 0; fileIndex < attachments.length; fileIndex += 1) {
        // It is fine to disable this lint rule here
        // https://eslint.org/docs/rules/no-await-in-loop#when-not-to-use-it
        // eslint-disable-next-line no-await-in-loop
        await dispatch("sendAttachment", fileIndex);
      }
    },

    async sendAttachment({ commit, state }, fileIndex) {
      const { token, form: { webformId }, files } = state;
      const attachment = files[fileIndex];

      const onUploadProgress = (progressEvent) => {
        const progress = (progressEvent.loaded / progressEvent.total) * 100;
        commit("setUploadPercentage", { fileIndex, value: progress });
      };

      commit("setUploadPercentage", { fileIndex, value: 0 });
      commit("setUploadFileIndex", fileIndex);
      commit("clearFileError", fileIndex);
      commit("setUploading", { fileIndex, status: true });
      try {
        await FormService
          .sendAttachment(token, attachment, webformId, onUploadProgress);
        commit("setFileDone", fileIndex);
      } catch (error) {
        commit("addFileError", { fileIndex, error });
      } finally {
        commit("setUploading", { fileIndex, status: false });
      }
    }
  },
  modules: {}
});
